/**
 * DOM-side academy music player (learn-side only).
 *
 * Uses its own AudioContext + MusicPlayer instance, completely independent
 * from the game-side MusicPlayer used in BattleScene.  Two AudioContext
 * instances running simultaneously is fine; browser handles mixing.
 *
 * Usage in main.ts:
 *   enterAcademy();   // starts academy BGM
 *   leaveAcademy();   // stops it
 *
 * Volume is controlled via the `ef:audio-volume` CustomEvent dispatched by
 * the settings UI, or by calling setLearnMusicVolume() directly.
 */

import { BgmPlayer } from '../game/audio/bgm-player';
import { loadProgress } from '../shared/progress';

let player: BgmPlayer | null = null;

/**
 * Read proMode from progress directly to avoid circular import with
 * settings.ts (settings.ts already pulls helpers from this module's siblings).
 * Default ON when undefined — boss directive: 教學要專業.
 */
function proModeOn(): boolean {
  const p = loadProgress();
  if (!p) return true;
  const v = p.parentSettings?.proMode;
  return v === undefined ? true : v;
}

/**
 * Start the academy BGM.
 * Stops any currently playing track first, then restarts with academy notes.
 * Creates the BgmPlayer singleton lazily on first call. BgmPlayer
 * auto-upgrades from synth to MP3 file mode if /assets/bgm/academy.mp3 loads.
 *
 * v24 — 教學專業模式 ON ⇒ no-op (no BGM). If a player was previously created
 * (proMode toggled mid-session), it gets stopped here too.
 */
export function enterAcademy(): void {
  if (proModeOn()) {
    if (player) player.stop();
    return;
  }
  if (!player) {
    player = new BgmPlayer();
  } else {
    player.stop();
  }
  player.playAcademyBgm();
  player.start();
}

/**
 * Stop the academy BGM.
 */
export function leaveAcademy(): void {
  if (player) {
    player.stop();
  }
}

/** Set BGM volume on the learn-side player (0..1). No-op if player not yet created. */
export function setLearnMusicVolume(v: number): void {
  if (player) player.setVolume(v);
}

// Listen for volume changes dispatched by the settings UI.
window.addEventListener('ef:audio-volume', (e) => {
  const detail = (e as CustomEvent<{ bgm: number; sfx: number }>).detail;
  if (player) player.setVolume(detail.bgm);
});

// P05: when main.ts dispatches `ef:mode-switch` from learn → battle, hard-stop
// the academy BGM so two BgmPlayer instances aren't simultaneously stealing
// CPU on iPad / 低階機. The battle-side BGM is created by HorizontalBattleScene
// itself; this listener only releases the learn-side player.
//
// Idempotent: leaveAcademy() is a no-op when player is null or already stopped.
window.addEventListener('ef:mode-switch', (e) => {
  const detail = (e as CustomEvent<{ from: 'learn' | 'battle' | null; to: 'learn' | 'battle' }>).detail;
  if (detail && detail.to === 'battle') {
    leaveAcademy();
  }
});

// v24 — proMode toggled mid-session ⇒ when user just turned proMode ON,
// kill any in-flight academy BGM immediately so they don't have to leave +
// re-enter the learn screen for music to stop. Toggle OFF is a no-op (the
// next enterAcademy() call from main.ts will start a fresh player).
window.addEventListener('ef:pro-mode-changed', (e) => {
  const detail = (e as CustomEvent<{ proMode: boolean }>).detail;
  if (detail?.proMode === true) leaveAcademy();
});
